/**
 * Exceptions List Page — Issue #345.
 *
 * Wraps the existing useExceptionsList + the new useExceptionEventResolveById
 * hook in the shared AdminDataTable. Exception events are audit-grade
 * laboratory records — the only non-physical removal semantic the backend
 * exposes is `resolved` (via POST /lab/exceptions/{id}/resolve), so 归档 /
 * 关闭 wires to that transition with a mandatory resolution note. No physical
 * deletion is exposed.
 *
 * Issue #345 audit fix2: useExceptionsList 仅接受 `{ page, limit, severity,
 * cellBatchId }`，之前在工具栏暴露的「关键字」文本输入只会过滤当前已加载的
 * 一页，会与后端返回的命中数不一致。在 `/lab/exceptions` 增加描述/类别模糊
 * 查询前，工具栏只保留 severity 下拉（服务端真实支持）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { Eye, Pencil, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  useExceptionsList,
  useExceptionEventResolveById,
  type ExceptionEventView,
} from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  canReadExceptionEvent,
  canUpdateExceptionEvent,
} from '@/lib/labPermissions';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';

type ExceptionSeverity = 'low' | 'medium' | 'high' | 'critical';

function isExceptionSeverity(value: string): value is ExceptionSeverity {
  return ['low', 'medium', 'high', 'critical'].includes(value);
}

const SEVERITY_OPTIONS: ReadonlyArray<{ value: ExceptionSeverity; label: string }> = [
  { value: 'critical', label: '严重 (Critical)' },
  { value: 'high', label: '高 (High)' },
  { value: 'medium', label: '中 (Medium)' },
  { value: 'low', label: '低 (Low)' },
];

const SEVERITY_BADGE: Record<ExceptionSeverity, string> = {
  critical: 'bg-red-100 text-red-900',
  high: 'bg-orange-100 text-orange-900',
  medium: 'bg-yellow-100 text-yellow-900',
  low: 'bg-blue-100 text-blue-900',
};

export default function ExceptionsListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch } = useExceptionsList();
  const { resolve, loading: resolving } = useExceptionEventResolveById();

  const [exceptions, setExceptions] = useState<ExceptionEventView[]>([]);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    severity: '',
  });

  const limit = 20;
  const severityFilter = (searchValues['severity'] ?? '') as ExceptionSeverity | '';

  const canRead = canReadExceptionEvent(permissions);
  const canUpdate = canUpdateExceptionEvent(permissions);

  const refresh = useCallback(async () => {
    if (!canRead) return;
    setPageLoading(true);
    try {
      const data = await fetch({
        page,
        limit,
        ...(severityFilter && isExceptionSeverity(severityFilter)
          ? { severity: severityFilter }
          : {}),
      });
      if (data) setExceptions(data.items || []);
    } catch (err: unknown) {
      toast.error(
        `加载异常事件失败：${err instanceof Error ? err.message : '未知错误'}`,
      );
    } finally {
      setPageLoading(false);
    }
  }, [canRead, fetch, page, severityFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Issue #345 audit fix2: 服务端按 severity 分页，禁止在当前页上叠加描述/类别
  // 文本筛选——否则命中条数和后端 total/翻页位置不一致。
  const visibleRows = exceptions;

  const columns: ReadonlyArray<AdminDataTableColumn<ExceptionEventView>> = useMemo(
    () => [
      {
        key: 'severity',
        header: '严重级别',
        alwaysVisible: true,
        render: (e) => {
          const sev = isExceptionSeverity(e.severity) ? e.severity : 'low';
          return (
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold ${SEVERITY_BADGE[sev]}`}
            >
              {e.severity.toUpperCase()}
            </span>
          );
        },
      },
      {
        key: 'category',
        header: '类别',
        render: (e) => e.category,
      },
      {
        key: 'description',
        header: '描述',
        render: (e) => (
          <span className="text-sm">{e.description || e.title}</span>
        ),
      },
      {
        key: 'status',
        header: '状态',
        render: (e) => e.status,
      },
      {
        key: 'cellBatchId',
        header: '批次 ID',
        defaultHidden: true,
        render: (e) => (
          <span className="font-mono text-xs">{e.cellBatchId ?? '—'}</span>
        ),
      },
      {
        key: 'reportedAt',
        header: '报告时间',
        render: (e) => new Date(e.reportedAt).toLocaleString('zh-CN'),
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<ExceptionEventView>> =
    useMemo(
      () => [
        {
          key: 'view',
          label: '查看详情',
          icon: <Eye className="h-3.5 w-3.5" />,
          onSelect: (e) => navigate(`/lab/exceptions/${e.id}`),
        },
        {
          key: 'edit',
          label: '处理',
          icon: <Pencil className="h-3.5 w-3.5" />,
          visible: () => canUpdate,
          onSelect: (e) => navigate(`/lab/exceptions/${e.id}`),
        },
        {
          key: 'resolve',
          label: '归档（解决并关闭）',
          icon: <ShieldCheck className="h-3.5 w-3.5" />,
          // 实验室异常事件不可物理删除（AI GUIDE §6）。归档 = 调用
          // POST /lab/exceptions/{id}/resolve 写入解决记录；后端会校验
          // 当前状态是否允许 resolve、是否需要二次复核。
          visible: (e) => canUpdate && e.status !== 'resolved',
          disabled: () => resolving,
          onSelect: async (e) => {
            const note = window.prompt(
              '请填写解决说明（必填，将与解决人一同写入审计日志）',
            );
            const trimmed = note?.trim();
            if (!trimmed) return;
            const ok = await resolve(e.id, e.version ?? 0, trimmed);
            if (ok) {
              toast.success('异常事件已归档');
              await refresh();
            }
          },
        },
      ],
      [canUpdate, navigate, refresh, resolve, resolving],
    );

  if (!canRead) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看异常事件的权限</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">异常事件</h1>
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          刷新
        </Button>
      </div>

      <AdminDataTable<ExceptionEventView>
        testIdPrefix="lab-exceptions"
        columns={columns}
        rows={visibleRows}
        isLoading={pageLoading}
        emptyMessage="暂无异常事件"
        filters={{
          fields: [
            {
              kind: 'select',
              name: 'severity',
              label: '严重级别',
              options: SEVERITY_OPTIONS.map((s) => ({
                value: s.value,
                label: s.label,
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ severity: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize: limit,
          total:
            exceptions.length === limit
              ? page * limit + 1
              : (page - 1) * limit + exceptions.length,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />
    </div>
  );
}
